import type { FC } from 'react';

import CaptureProfileUsersName from '@zen/Accounts/CaptureProfileUsersName';
import AuditLogsSlideOut from '@zen/Audit/AuditLogs/AuditLogsSlideOut';
import useRole from '@zen/Auth/hooks/useRole';
import useGlobalPermissions from '@zen/Auth/useGlobalPermissions';
import ErrorBoundary from '@zen/Components/ErrorBoundary/ErrorBoundary';
import NetPromoterScoreBanner from '@zen/Components/NetPromoterScoreBanner';
import { growthBookClient, useFeatureIsOn } from '@zen/FeatureFlag';
import { AccountStatus, Role } from '@zen/graphql/types.generated';
import Sidebar from '@zen/Layout/Sidebar';
import TopNavigation from '@zen/Layout/TopNavigation';
import { authRoutes } from '@zen/Routes';
import useAccount from '@zen/utils/hooks/useAccount';
import useDeepCompareEffect from '@zen/utils/hooks/useDeepCompareEffect';
import useTracking from '@zen/utils/hooks/useTracking';
import { useNavigationHistory } from '@zen/utils/NavigationHistory';

import DashboardRoutes from '../DashboardRoutes';

const Dashboard: FC = () => {
  const { accountUuid, accountStatus, userProfile, refetchUser } = useAccount();
  const userRole = useRole();
  const { check } = useGlobalPermissions();
  const { identify } = useTracking();
  const role = useRole();
  const isAuditLogsEnabled: boolean = useFeatureIsOn('audit-logs');
  const isNpsSurveyEnabled: boolean = useFeatureIsOn('nps-survey');

  const { navigate } = useNavigationHistory();

  const canSwitchAccount: boolean = check('accounts.canSwitch');
  const canSubmitNpsSurvey: boolean = userRole !== Role.ADMIN;

  useDeepCompareEffect(() => {
    identify(accountUuid, userProfile, role);

    growthBookClient.setAttributes({
      accountId: accountUuid,
      id: userProfile.uuid,
      userRole: role
    });
  }, [identify, accountUuid, userProfile, role]);

  if (canSwitchAccount && !accountUuid) {
    navigate(authRoutes.accountSwitcher.getUrl());

    return null;
  }

  if (!userProfile.isProfileCompleted) {
    return <CaptureProfileUsersName onSuccess={refetchUser} />;
  }

  if (accountStatus === AccountStatus.PENDING) {
    navigate(authRoutes.accountActivation.getUrl());

    return null;
  }

  return (
    <div className="flex flex-col h-full bg-white" data-testid="main-application">
      {isAuditLogsEnabled && <AuditLogsSlideOut />}
      <TopNavigation />
      <div className="flex flex-row">
        <Sidebar />
        <div className="flex-1 min-w-0">
          {isNpsSurveyEnabled && canSubmitNpsSurvey && <NetPromoterScoreBanner />}
          <ErrorBoundary>
            <DashboardRoutes />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
